import { useState } from "react";
import { motion } from "framer-motion";
import {
    ArrowRight, ShieldCheck, Mic, Users, Film, Lock,
} from "lucide-react";
import { FreeBadge } from "@/components/FreeBadge";
import { FreeStartDialog } from "@/components/landing/FreeStartDialog";
import { HeroFamilyTree } from "@/components/landing/HeroFamilyTree";
import { useLang } from "@/contexts/LanguageContext";

const fadeUp = {
    hidden: { opacity: 0, y: 24 },
    show: (i = 0) => ({ opacity: 1, y: 0, transition: { duration: 0.55, delay: i * 0.12, ease: "easeOut" } }),
};
const TRUST_POINTS = [
    { icon: Lock,        label: "trustSecure" },
    { icon: ShieldCheck, label: "trustPrivate" },
    { icon: Mic,         label: "trustLangs" },
    { icon: Users,       label: "trustFree" },
];

// (removed fabricated STATS constant — real counts now come from /api/public/platform-stats)

function HeroCopy() {
    const { t } = useLang();
    const [dialogOpen, setDialogOpen] = useState(false);
    return (
        <div className="lg:col-span-6">
            {/* Social-proof pill — instant credibility above the headline */}
            <motion.div
                variants={fadeUp} initial="hidden" animate="show" custom={0.5}
                className="inline-flex items-center gap-2 mb-3 px-3.5 py-1.5 rounded-full bg-white border border-[#6D28D9]/15 shadow-sm"
                data-testid="hero-social-proof"
            >
                <span className="flex -space-x-1.5">
                    {["#6D28D9", "#EF4444", "#4C1D95", "#C9A55B"].map((c) => (
                        <span key={c} className="inline-block h-5 w-5 rounded-full border-2 border-white" style={{ background: c }} />
                    ))}
                </span>
                <span className="text-[12.5px] font-medium text-[#EF4444] inline-flex items-center gap-1">
                    8000+ Indian families trust us
                </span>
            </motion.div>

            <motion.h1
                variants={fadeUp} initial="hidden" animate="show" custom={0.8}
                className="font-extrabold tracking-tight leading-[1.1] text-[#6D28D9]"
                style={{ fontSize: "clamp(21px, 3vw, 36px)" }}
                data-testid="hero-h1"
            >
                ONE INDIA. THOUSANDS OF TRADITIONS.{" "}
                <span className="text-[#1B5E20]">ONE FAMILY REPOSITORY.</span>
            </motion.h1>
            <motion.p
                variants={fadeUp} initial="hidden" animate="show" custom={1.2}
                className="mt-2.5 text-[15px] sm:text-[17px] font-semibold text-[#1F2937]"
                data-testid="hero-subline"
            >
                Created in India. By Indians. For every Indian family.
            </motion.p>
            <motion.p
                variants={fadeUp} initial="hidden" animate="show" custom={1.6}
                className="mt-2 text-[14.5px] sm:text-base leading-relaxed text-[#4B5563] max-w-xl"
                data-testid="hero-description"
            >
                Preserve your religion, culture, traditions, rituals, family history and unique heritage—your way.
            </motion.p>
            <motion.p
                variants={fadeUp} initial="hidden" animate="show" custom={2}
                className="mt-2 text-[12.5px] sm:text-[13px] leading-relaxed text-[#6B7280] max-w-xl"
                data-testid="hero-support-line"
            >
                With <span className="font-semibold text-[#1B5E20]">1,000+ preservation areas</span> and the freedom to add your own, there is a place here for every Indian family&apos;s unique legacy.
            </motion.p>

            <motion.div
                variants={fadeUp} initial="hidden" animate="show" custom={3}
                className="mt-4 sm:mt-5 flex flex-col sm:flex-row items-stretch sm:items-center gap-3"
            >
                <button
                    data-testid="hero-cta-primary"
                    onClick={() => setDialogOpen(true)}
                    className="group inline-flex items-center justify-center gap-2 bg-gradient-to-r from-[#7C3AED] to-[#6D28D9] text-white hover:from-[#6D28D9] hover:to-[#5B21B6] rounded-full px-6 sm:px-7 py-3 sm:py-3.5 text-[15px] sm:text-base font-semibold shadow-[0_10px_24px_rgba(109,40,217,0.32)] transition-all hover:-translate-y-0.5 hover:shadow-[0_14px_30px_rgba(109,40,217,0.4)] relative w-full sm:w-auto"
                >
                    <span>Start Preserving - Your Family Legacy</span>
                    <ArrowRight className="h-5 w-5 transition-transform group-hover:translate-x-1" />
                    <span className="absolute -top-2 -right-2">
                        <FreeBadge size="sm" />
                    </span>
                </button>
                <a
                    type="button"
                    data-testid="hero-cta-demos"
                    href="/demos"
                    title="Documentry"
                    aria-label="Documentry"
                    className="inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl bg-white border border-[#E5E7EB] text-[#6D28D9] shadow-sm hover:bg-[#EDE9FE] transition-colors self-center sm:self-auto"
                >
                    <Film className="h-5 w-5" />
                </a>
            </motion.div>

            <motion.div
                variants={fadeUp} initial="hidden" animate="show" custom={4}
                className="mt-4 sm:mt-5 flex flex-wrap items-center gap-x-5 gap-y-2 text-[13px] sm:text-sm text-[#4B5563]"
            >
                {TRUST_POINTS.map((p) => (
                    <span key={p.label} className="inline-flex items-center gap-2">
                        <p.icon className="h-4 w-4 text-[#1B5E20]" /> {t(p.label)}
                    </span>
                ))}
            </motion.div>

            <FreeStartDialog open={dialogOpen} onOpenChange={setDialogOpen} />
        </div>
    );
}

/** Video-poster style overlay card removed on 2026-08-06 per user request. */

/**
 * HeroVisual — always renders the bundled quote-card carousel (HeroFamilyTree).
 * Admin-uploaded /api/landing/hero-images is intentionally IGNORED here because
 * old prod data (a "Welcome" banner) was overriding the quote carousel and
 * causing a flash-of-fallback-then-swap on load. Admins now upload wide banners
 * via the "hero_top" position which renders as the full-width top strip.
 */
function HeroVisual() {
    return <HeroFamilyTree />;
}

export const Hero = () => (
    <section className="relative overflow-hidden bg-gradient-to-b from-[#F5FBF4] to-white lg:min-h-[calc(100svh-470px)] lg:flex lg:items-center" data-testid="landing-hero">
        <div className="absolute -top-32 -right-32 h-96 w-96 rounded-full bg-[#EDE9FE] blur-3xl opacity-70 pointer-events-none" />
        <div className="absolute top-40 -left-24 h-72 w-72 rounded-full bg-[#FEE2E2] blur-3xl opacity-70 pointer-events-none" />
        <div className="max-w-7xl w-full mx-auto px-4 sm:px-6 lg:px-8 py-4 sm:py-5 lg:py-6 grid grid-cols-1 lg:grid-cols-12 gap-6 items-center relative">
            <HeroCopy />
            <HeroVisual />
        </div>
    </section>
);
